import { RiscoPreEclampsiaTag } from '@atendimento/detail/soap/pre-natal/components/RiscoPreEclampsiaTag'
import { SdcFatoresRiscoPreEclampsiaModal } from '@atendimento/detail/soap/pre-natal/components/SdcFatoresRiscoPreEclampsiaModal'
import { css } from '@emotion/react'
import { Cell, Flow, Grid, HFlow, Icon, Text, VFlow } from 'bold-ui'
import { purple } from 'bold-ui/lib/styles/colors'
import { booleanToSimNaoEnum, simNaoEnumRecord } from 'components/form/field/SimNaoRadioGroupField'
import { HLabel } from 'components/HLabel'
import { useFlags } from 'config/useFlagsContext'
import { isAfter } from 'date-fns'
import {
  useAcompanhamentoPreNatalCidadaoQuery,
  useDataUltimaConsultaOdontoGestanteQuery,
} from 'graphql/hooks.generated'
import { AtendimentoProfissional, FonteIdadeGestacionalEnum, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { useFatoresRiscoPreEclampsiaModal } from 'hooks/sdc/useFatoresRiscoPreEclampsiaModal'
import { tipoSanguineoEnum } from 'types/enums'
import { calculateIdadeGestacional } from 'util/atendimento/gestante/calculateIdadeGestacional'
import { calculateDpp } from 'util/date/calculateDpp'
import { dateAsDdMmYyyy, formatDate, toDate } from 'util/date/formatDate'
import { ImprimirPreNatalButton } from 'view/atendimentos/detail/soap/aside/pre-natal/components/ImprimirPreNatalButton'
import { IdadeGestacionalAcompanhamentoPreNatal } from 'view/atendimentos/detail/soap/aside/pre-natal/model-acompanhamentoPreNatal'
import { RiscoGravidezTag } from 'view/atendimentos/detail/soap/pre-natal/components/RiscoGravidezTag'
import { formatIdadeGestacional } from 'view/atendimentos/detail/soap/pre-natal/util-preNatal'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'

interface InformacoesGestacaoAtualCardProps {
  cidadaoId: ID
  prontuarioId: ID
  atendimentoId: ID
  loading: boolean
  dum: Date
  idadeGestacional: IdadeGestacionalAcompanhamentoPreNatal
  dataInicioGestacao: Date
  fonteIdadeGestacional: FonteIdadeGestacionalEnum
  dppEcografica: Date
  atendimentosProfUltimaGestacao: AtendimentoProfissional[]
  dataReferencia: Instant
}

export function InformacoesGestacaoAtualCard(props: InformacoesGestacaoAtualCardProps) {
  const {
    cidadaoId,
    prontuarioId,
    atendimentoId,
    loading,
    dum,
    idadeGestacional,
    dataInicioGestacao,
    fonteIdadeGestacional,
    dppEcografica,
    atendimentosProfUltimaGestacao,
    dataReferencia,
  } = props

  const {
    data: { cidadao: { prontuario = null, tipoSanguineoEnum: tipoSanguineoCidadao } = {} },
    loading: loadingCidadao,
  } = useAcompanhamentoPreNatalCidadaoQuery({ variables: { id: cidadaoId }, fetchPolicy: 'cache-first' })

  const { dpp: dppCronologica } = calculateDpp(dum)

  const consultasValidas = atendimentosProfUltimaGestacao?.filter((atendProf: AtendimentoProfissional) =>
    isAfter(atendProf.iniciadoEm, dataInicioGestacao)
  )

  const quantidadeConsultas = consultasValidas?.length
  const dataPrimeiraConsulta = consultasValidas?.first()?.iniciadoEm
  const dataUltimaConsulta = consultasValidas?.last()?.iniciadoEm
  const igPrimeiraConsulta = calculateIdadeGestacional(dataInicioGestacao, toDate(dataPrimeiraConsulta))

  const {
    data: { dataUltimaConsultaOdontoGestante },
    loading: loadingDataOdonto,
  } = useDataUltimaConsultaOdontoGestanteQuery({
    variables: { input: { prontuarioId, cidadaoId, dataInicioGestacao: dataInicioGestacao } },
    skip: !prontuarioId || !dataInicioGestacao,
    fetchPolicy: 'cache-first',
  })

  const { PILOTO_SDC_PRE_ECLAMPSIA_ENABLED, PILOTO_RISCOS_SDC_ENABLED } = useFlags()
  const {
    isModalFatoresRiscoPreEclampsiaOpen,
    openModalFatoresRiscoPreEclampsia,
    closeModalFatoresRiscoPreEclampsia,
  } = useFatoresRiscoPreEclampsiaModal()

  const styles = createStyles()

  const headerContent = (
    <HFlow justifyContent='space-between' alignItems='center'>
      <RiscoGravidezTag isAltoRisco={prontuario?.preNatalAtivo?.altoRisco} />
      <ImprimirPreNatalButton
        atendimentoId={atendimentoId}
        prontuarioId={prontuarioId}
        dataAtendimento={dataReferencia}
      />
    </HFlow>
  )

  return (
    <AcompanhamentoCard
      id='informacoes-gestacao-atual'
      title='Informações da gestação atual'
      tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
      loading={loading || loadingCidadao || loadingDataOdonto}
      headerContent={headerContent}
      headerStyle={css`
        grid-template-columns: auto 1fr;
        padding: 0.25rem 1rem;
      `}
    >
      <Grid gap={0}>
        <Cell size={4} style={[styles.item]}>
          <VFlow vSpacing={0.5}>
            <HLabel title='DUM:'>{dateAsDdMmYyyy(dum)}</HLabel>

            <HFlow hSpacing={0.5} alignItems='center'>
              <HLabel title='IG (DUM):'>{formatIdadeGestacional(idadeGestacional?.cronologicaEmDias)}</HLabel>

              {fonteIdadeGestacional === FonteIdadeGestacionalEnum.DUM && (
                <Icon icon='checkCircleOutline' color={purple.c60} size={1} />
              )}
            </HFlow>

            <HLabel title='DPP (DUM):'>{dateAsDdMmYyyy(dppCronologica)}</HLabel>

            <HLabel title='IG 1ª consulta:'>{formatIdadeGestacional(igPrimeiraConsulta.emDias)}</HLabel>
          </VFlow>
        </Cell>

        <Cell size={4} style={[styles.item]}>
          <VFlow vSpacing={0.5}>
            <HFlow hSpacing={0.5} alignItems='center'>
              <HLabel title='IG (eco. obst.):'>{formatIdadeGestacional(idadeGestacional?.ecograficaEmDias)}</HLabel>

              {fonteIdadeGestacional === FonteIdadeGestacionalEnum.US && (
                <Icon icon='checkCircleOutline' color={purple.c60} size={1} />
              )}
            </HFlow>

            <HLabel title='DPP (eco. obst.):'>{dppEcografica && dateAsDdMmYyyy(dppEcografica)}</HLabel>

            <HLabel title='Tipo de gravidez:'>{prontuario?.preNatalAtivo?.tipoGravidez?.descricao}</HLabel>

            <HLabel title='Gravidez planejada:'>
              {simNaoEnumRecord[booleanToSimNaoEnum(prontuario?.preNatalAtivo?.gravidezPlanejada)]}
            </HLabel>
          </VFlow>
        </Cell>

        <Cell size={4} style={[styles.item]}>
          <VFlow vSpacing={0.5}>
            <HLabel title='Consultas:'>
              {quantidadeConsultas} | Última em {formatDate(dataUltimaConsulta)}
            </HLabel>

            <HLabel title='Última consulta odontológica:'>
              {dataUltimaConsultaOdontoGestante && formatDate(dataUltimaConsultaOdontoGestante)}
            </HLabel>

            <HLabel title='Tipo sanguíneo:'>{tipoSanguineoCidadao && tipoSanguineoEnum[tipoSanguineoCidadao]}</HLabel>
            {PILOTO_SDC_PRE_ECLAMPSIA_ENABLED && PILOTO_RISCOS_SDC_ENABLED && (
              <Flow direction='vertical' gap={0}>
                <HLabel title='Risco de pré-eclâmpsia:'>
                  <RiscoPreEclampsiaTag prontuarioId={prontuarioId} />
                </HLabel>
                <Text
                  color='primary'
                  textDecoration='underline'
                  style={styles.pointer}
                  onClick={openModalFatoresRiscoPreEclampsia}
                >
                  Ver fatores analisados
                </Text>
                <SdcFatoresRiscoPreEclampsiaModal
                  modalOpen={isModalFatoresRiscoPreEclampsiaOpen}
                  setModalOpen={closeModalFatoresRiscoPreEclampsia}
                />
              </Flow>
            )}
          </VFlow>
        </Cell>
      </Grid>
    </AcompanhamentoCard>
  )
}

const createStyles = () => {
  return {
    item: css`
      padding: 1rem 0.5rem;
    `,
    pointer: css`
      cursor: pointer;
    `,
  }
}
